import { apiSlice } from './apiSlice'
import type { Case } from './casesApi'

export interface Team {
  id: string
  name: string
  university: string
  members: string[]
  manager_id?: string
  coach_id?: string
  status: string
  assigned_cases?: string[]
  created_at?: string
  updated_at?: string
  profile_text?: string | null
  guest_visible?: boolean
  rejection_reason?: string | null
  reviewed_at?: string | null
  reviewed_by?: string | null
  member_cards?: Record<string, MemberCardInfo>
}

export interface TeamReviewBody {
  decision: 'approve' | 'reject'
  reason?: string
}

export interface TeamCreate {
  name: string
  university: string
  members: string[]
  coach_id?: string
  profile_text?: string
  member_cards?: Record<string, MemberCardInfo>
}

export interface MemberCardInfo {
  storage_path: string
  file_name: string
  mime_type: string
  uploaded_at: string
  uploaded_by: string
}

export const teamsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<Team[], void>({
      query: () => '/teams/list',
      providesTags: ['Team'],
    }),
    getTeam: builder.query<Team, string>({
      query: (id) => `/teams/${id}`,
      providesTags: (result, error, id) => [{ type: 'Team', id }],
    }),
    createTeam: builder.mutation<Team, TeamCreate>({
      query: (body) => ({
        url: '/teams/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team'],
    }),
    updateTeam: builder.mutation<Team, { id: string } & Partial<TeamCreate>>({
      query: ({ id, ...data }) => ({
        url: `/teams/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Team', id }],
    }),
    reviewTeam: builder.mutation<Team, { teamId: string } & TeamReviewBody>({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/review`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { teamId }) => ['Team', { type: 'Team', id: teamId }],
    }),
    uploadMemberCard: builder.mutation<MemberCardInfo, { memberId: string; file: File }>({
      query: ({ memberId, file }) => {
        const formData = new FormData()
        formData.append('member_id', memberId)
        formData.append('file', file)
        return {
          url: '/teams/member-card/upload',
          method: 'POST',
          body: formData,
        }
      },
    }),
    getTeamCases: builder.query<Case[], string>({
      query: (teamId) => `/teams/${teamId}/cases`,
      providesTags: (result, error, teamId) => [{ type: 'Case', id: `team-${teamId}` }],
    }),
  }),
})

export const {
  useGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useReviewTeamMutation,
  useUploadMemberCardMutation,
  useGetTeamCasesQuery,
} = teamsApi
